/**
 * Pricing Page Component
 * 
 * Displays available subscription plans and one-time purchases.
 */

import React, { useState } from 'react';
import { Check, Sparkles, Zap, Crown, X } from 'lucide-react';
import { usePaymentCapabilities, useProducts } from '../../hooks/usePayments';
import { Product, ProductType, BillingInterval, formatPrice, formatBillingInterval } from '../../types/payments';
import { useThemeStore } from '../../stores/themeStore';
import { CheckoutModal } from './CheckoutModal';

interface PricingCardProps {
  product: Product;
  isPopular?: boolean;
  onSelect: () => void;
  isDark: boolean;
}

const PricingCard: React.FC<PricingCardProps> = ({ product, isPopular, onSelect, isDark }) => {
  const isSubscription = product.product_type === ProductType.SUBSCRIPTION;
  const isYearly = product.billing_interval === BillingInterval.YEARLY;
  
  return (
    <div className={`
      relative flex flex-col rounded-2xl border-2 p-6 transition-all duration-300
      ${isPopular 
        ? 'border-cyan-500 shadow-xl shadow-cyan-500/20' 
        : isDark 
          ? 'border-white/10 hover:border-white/20' 
          : 'border-slate-200 hover:border-slate-300'
      }
      ${isDark ? 'bg-slate-800/50' : 'bg-white'}
    `}>
      {/* Popular badge */}
      {isPopular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 text-white text-sm font-semibold shadow-lg">
            <Sparkles className="w-4 h-4" />
            Most Popular
          </span>
        </div>
      )}
      
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          {isYearly ? (
            <Crown className="w-5 h-5 text-amber-500" />
          ) : isSubscription ? (
            <Zap className="w-5 h-5 text-cyan-500" />
          ) : (
            <Sparkles className="w-5 h-5 text-purple-500" />
          )}
          <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            {product.name}
          </h3>
        </div>
        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          {product.description}
        </p>
      </div>
      
      {/* Price */}
      <div className="mb-6">
        <div className="flex items-baseline gap-1">
          <span className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            {formatPrice(product.price_amount, product.currency)}
          </span>
          {isSubscription && (
            <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              /{formatBillingInterval(product.billing_interval)}
            </span>
          )}
        </div>
        {isYearly && (
          <p className="mt-1 text-sm text-emerald-500 font-medium">
            Save 33% compared to monthly
          </p>
        )}
      </div>
      
      {/* Features */}
      <ul className="space-y-3 mb-8 flex-1">
        {product.features?.map((feature, index) => (
          <li key={index} className="flex items-start gap-2">
            <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              {feature}
            </span>
          </li>
        ))}
      </ul>
      
      {/* CTA Button */}
      <button
        onClick={onSelect}
        className={`
          w-full py-3 px-6 rounded-xl font-semibold transition-all duration-200
          ${isPopular
            ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white hover:shadow-lg hover:shadow-cyan-500/30'
            : isDark
              ? 'bg-white/10 text-white hover:bg-white/20'
              : 'bg-slate-100 text-slate-800 hover:bg-slate-200'
          }
        `}
      >
        {isSubscription ? 'Subscribe Now' : 'Buy Now'}
      </button>
    </div>
  );
};

interface PricingPageProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PricingPage: React.FC<PricingPageProps> = ({ isOpen, onClose }) => {
  const theme = useThemeStore((state) => state.theme);
  const isDark = theme === 'dark';
  
  const { data: paymentCapabilities, isLoading: isLoadingCapabilities } = usePaymentCapabilities(isOpen);
  const { data: productsData, isLoading, error } = useProducts();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  
  if (!isOpen) return null;
  
  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product);
    setShowCheckout(true);
  };
  
  const handleCloseCheckout = () => {
    setShowCheckout(false);
    setSelectedProduct(null);
  };
  
  const handlePaymentSuccess = () => {
    setShowCheckout(false);
    setSelectedProduct(null);
    onClose();
    // TODO: Show success message and refresh entitlements
  };

  const subscriptionsPublicEnabled = Boolean(paymentCapabilities?.subscriptions_public_enabled);
  const oneTimeProducts = productsData?.products.filter(p => p.product_type === ProductType.ONE_TIME) || [];
  const oneTimePurchases = subscriptionsPublicEnabled ? oneTimeProducts : [];
  
  // Separate products by type
  const subscriptions = subscriptionsPublicEnabled
    ? productsData?.products.filter(p => p.product_type === ProductType.SUBSCRIPTION) || []
    : [];
  
  // Find the most popular product (yearly subscription)
  const popularProductId = subscriptions.find(p => p.billing_interval === BillingInterval.YEARLY)?.id;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={`
        relative w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto rounded-2xl p-8
        ${isDark ? 'bg-slate-900' : 'bg-slate-50'}
      `}>
        {/* Close button */}
        <button
          onClick={onClose}
          className={`
            absolute top-4 right-4 p-2 rounded-full transition-colors
            ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-slate-200 text-slate-500'}
          `}
        >
          <X className="w-6 h-6" />
        </button>
        
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className={`text-3xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-800'}`}>
            Choose Your Plan
          </h2>
          <p className={`text-lg ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Unlock unlimited AI tutoring and accelerate your learning
          </p>
        </div>
        
        {isLoading || isLoadingCapabilities ? (
          <div className="flex justify-center py-12">
            <div className="w-10 h-10 border-3 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500">Failed to load pricing. Please try again.</p>
          </div>
        ) : (
          <>
            {/* Subscription Plans */}
            {subscriptions.length > 0 && (
              <div className="mb-12">
                <h3 className={`text-xl font-semibold mb-6 text-center ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  Subscription Plans
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                  {subscriptions.map(product => (
                    <PricingCard
                      key={product.id}
                      product={product}
                      isPopular={product.id === popularProductId}
                      onSelect={() => handleSelectProduct(product)}
                      isDark={isDark}
                    />
                  ))}
                </div>
              </div>
            )}

            {!subscriptionsPublicEnabled && (
              <div className={`mb-12 rounded-2xl border px-6 py-5 text-center ${
                isDark
                  ? 'border-amber-500/20 bg-amber-500/10 text-amber-100'
                  : 'border-amber-200 bg-amber-50 text-amber-900'
              }`}>
                <h3 className="text-lg font-semibold">Subscription billing is not public yet</h3>
                <p className={`mt-2 text-sm ${isDark ? 'text-amber-100/80' : 'text-amber-900/80'}`}>
                  The current launch supports subject access through EcoCash. Subscription and customer-portal
                  billing will return after the public payments rollout is complete.
                </p>
              </div>
            )}
            
            {/* One-time Purchases */}
            {oneTimePurchases.length > 0 && (
              <div>
                <h3 className={`text-xl font-semibold mb-6 text-center ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  One-time Purchases
                </h3>
                <div className={`grid gap-6 max-w-3xl mx-auto ${
                  oneTimePurchases.length === 1 
                    ? 'grid-cols-1 max-w-sm' 
                    : oneTimePurchases.length === 2 
                      ? 'grid-cols-1 md:grid-cols-2' 
                      : 'grid-cols-1 md:grid-cols-3'
                }`}>
                  {oneTimePurchases.map(product => (
                    <PricingCard
                      key={product.id}
                      product={product}
                      onSelect={() => handleSelectProduct(product)}
                      isDark={isDark}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        
        {/* Trust badges */}
        <div className={`mt-12 pt-8 border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
          <div className="flex flex-wrap justify-center items-center gap-8 text-sm">
            {subscriptionsPublicEnabled ? (
              <>
                <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                  🔒 Secure payments via Stripe
                </span>
                <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                  💳 All major cards accepted
                </span>
                <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                  ✨ Cancel anytime
                </span>
              </>
            ) : (
              <>
                <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                  📱 EcoCash subject access available at launch
                </span>
                <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                  🔐 Payment rollout is being staged safely
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Checkout Modal */}
      {showCheckout && selectedProduct && (
        <CheckoutModal
          product={selectedProduct}
          onClose={handleCloseCheckout}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

export default PricingPage;
